/**
 * Hand-history GTO analyzer.
 *
 * Walks each parsed hand through the same GTO pipeline the Trainer uses:
 *  - preflop decisions graded against the local preflop charts (opens.js)
 *  - postflop decisions graded against the V6 solver tree (heads-up spots only)
 *
 * Size matching rules:
 *  - Opponents: snapped to the nearest tree action.
 *  - Hero: strict - preflop raise sizes must match a tree size to the nearest
 *    0.5bb, postflop to the nearest 1bb. Otherwise the hand leaves the GTO
 *    tree and every later decision is rated N/A (excluded from stats).
 */
import { getPreflopDecisionGrid } from "../preflop/opens";
import { PREFLOP_ORDER } from "./ggParser";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";
const SOLVER_V6_VERSION_ID = process.env.REACT_APP_SOLVER_TRAINER_V6_VERSION_ID || process.env.REACT_APP_SOLVER_V6_VERSION_ID || "v6-final";
const V6_CONTEXT = {
  mode: "cash",
  tableSize: (process.env.REACT_APP_SOLVER_CASH_TABLE_SIZE || "6handed").toLowerCase(),
  stackSize: (process.env.REACT_APP_SOLVER_DEFAULT_STACK_SIZE || "100bbs").toLowerCase(),
  openSize: (process.env.REACT_APP_SOLVER_CASH_OPEN_SIZE || "2.5x").toLowerCase(),
};

const SEAT_CODE = { lj: "LO", hj: "HI", co: "CO", btn: "BTN", sb: "SB", bb: "BB" };
const LEGACY_SPOT_LOOKUP_REMAP = {
  LOopenHIcall: "HIopenCOcall",
  LOopenCOcall: "HIopenCOcall",
  LOopenBTNcall: "HIopenBTNcall",
  LOopenSBcall: "HIopenSBcall",
  LOopenBBcall: "HIopenBBcall",
  HI3betLOcall: "CO3betHIcall",
  CO3betLOcall: "CO3betHIcall",
  BTN3betLOcall: "BTN3betHIcall",
  SB3betLOcall: "SB3betHIcall",
  BB3betLOcall: "BB3betHIcall",
};

const SOLVER_RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const SOLVER_SUITS = ["s", "h", "d", "c"];
const CANONICAL_SUITS = ["h", "d", "c", "s"];
const RANK_VALUE = { A: 14, K: 13, Q: 12, J: 11, T: 10, "9": 9, "8": 8, "7": 7, "6": 6, "5": 5, "4": 4, "3": 3, "2": 2 };
const STRAIGHT_WINDOWS = [
  [14, 13, 12, 11, 10], [13, 12, 11, 10, 9], [12, 11, 10, 9, 8], [11, 10, 9, 8, 7],
  [10, 9, 8, 7, 6], [9, 8, 7, 6, 5], [8, 7, 6, 5, 4], [7, 6, 5, 4, 3], [6, 5, 4, 3, 2], [14, 5, 4, 3, 2],
];

const HERO_PREFLOP_TOLERANCE_BB = 0.5 + 1e-6; // hero raise must be within 0.5bb of a tree size
const HERO_POSTFLOP_TOLERANCE_BB = 1.0 + 1e-6; // hero bet/raise must be within 1bb of a tree size

// ─── Generic card / combo helpers (mirrors Trainer.js module helpers) ────────

function extractNumber(label) {
  const nums = String(label || "").match(/\d+(?:\.\d+)?/g);
  if (!nums?.length) return 0;
  return Number(nums[nums.length - 1]) || 0;
}

function cardSortValue(card) {
  const ranks = "23456789TJQKA";
  const suits = "cdhs";
  return ranks.indexOf(card?.[0] || "2") * 4 + suits.indexOf(card?.[1] || "c");
}

function canonicalComboKey(a, b) {
  return [a, b].filter(Boolean).sort((x, y) => cardSortValue(x) - cardSortValue(y)).join("");
}

function rankOrderIndex(rank) {
  return SOLVER_RANKS.indexOf(rank);
}

export function handLabelFromTwoCards(cards) {
  const c1 = cards?.[0];
  const c2 = cards?.[1];
  if (!c1 || !c2 || c1.length < 2 || c2.length < 2) return null;
  const r1 = c1[0];
  const r2 = c2[0];
  const i1 = rankOrderIndex(r1);
  const i2 = rankOrderIndex(r2);
  if (i1 < 0 || i2 < 0) return null;
  if (r1 === r2) return `${r1}${r2}`;
  const hi = i1 < i2 ? r1 : r2;
  const lo = i1 < i2 ? r2 : r1;
  return `${hi}${lo}${c1[1] === c2[1] ? "s" : "o"}`;
}

function findCellByLabel(grid, label) {
  if (!grid || !label) return null;
  for (const row of grid) {
    for (const cell of row || []) {
      if (String(cell?.label || "").toLowerCase() === String(label || "").toLowerCase()) return cell;
    }
  }
  return null;
}

function getFrequenciesForHand(cell, handCards) {
  const combos = cell?.combos || [];
  const targetKey = canonicalComboKey(handCards?.[0], handCards?.[1]);
  const exact = combos.find((c) => canonicalComboKey(String(c?.hand || "").slice(0, 2), String(c?.hand || "").slice(2, 4)) === targetKey);
  if (exact?.frequencies) return { frequencies: exact.frequencies, exactMatch: true };
  return { frequencies: cell?.frequencies || [], exactMatch: false };
}

function canonicalizeComboHand(comboHand) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const r1 = comboHand[0];
  const s1 = String(comboHand[1] || "").toLowerCase();
  const r2 = comboHand[2];
  const s2 = String(comboHand[3] || "").toLowerCase();
  const rankOrder = (r) => SOLVER_RANKS.indexOf(r);
  const suitOrder = (s) => SOLVER_SUITS.indexOf(s);
  if (rankOrder(r2) !== -1 && rankOrder(r1) !== -1 && rankOrder(r2) < rankOrder(r1)) return `${r2}${s2}${r1}${s1}`;
  if (r1 === r2 && suitOrder(s1) > suitOrder(s2)) return `${r2}${s2}${r1}${s1}`;
  return `${r1}${s1}${r2}${s2}`;
}

function comboToHandCategory(combo) {
  if (combo?.length !== 4) return null;
  const [rank1, suit1, rank2, suit2] = [combo[0], combo[1], combo[2], combo[3]];
  const r1idx = SOLVER_RANKS.indexOf(rank1);
  const r2idx = SOLVER_RANKS.indexOf(rank2);
  if (r1idx === -1 || r2idx === -1) return null;
  if (rank1 === rank2) return rank1 + rank1;
  if (suit1 === suit2) return r1idx < r2idx ? rank1 + rank2 + "s" : rank2 + rank1 + "s";
  return r1idx < r2idx ? rank1 + rank2 + "o" : rank2 + rank1 + "o";
}

function isPocketPairCombo(comboStr) {
  return comboStr?.length === 4 && comboStr[0] === comboStr[2];
}

function isPrimaryApiComboUsable(apiComboStr, comboData, desiredComboHand = null) {
  if (!apiComboStr || !comboData?.frequencies?.length) return false;
  const cat = comboToHandCategory(apiComboStr);
  const mappedIsPairCategory = !!(cat && cat.length === 2 && cat[0] === cat[1]);
  if (mappedIsPairCategory && !isPocketPairCombo(desiredComboHand || "")) return false;
  return true;
}

// ─── Board / suit mapping (real board -> queried DB board) ──────────────────

function createSuitMapping(flopCards) {
  const userSuits = [];
  for (const card of flopCards || []) {
    const suit = card?.[1]?.toLowerCase();
    if (!suit) continue;
    if (!userSuits.includes(suit)) userSuits.push(suit);
  }
  const toCanonical = {};
  const fromCanonical = {};
  for (let i = 0; i < userSuits.length; i++) {
    toCanonical[userSuits[i]] = CANONICAL_SUITS[i];
    fromCanonical[CANONICAL_SUITS[i]] = userSuits[i];
  }
  const remainingUserSuits = SOLVER_SUITS.filter((s) => !userSuits.includes(s));
  const remainingCanonicalSuits = CANONICAL_SUITS.filter((s) => !Object.values(toCanonical).includes(s));
  for (let i = 0; i < remainingUserSuits.length; i++) {
    toCanonical[remainingUserSuits[i]] = remainingCanonicalSuits[i];
    fromCanonical[remainingCanonicalSuits[i]] = remainingUserSuits[i];
  }
  return { toCanonical, fromCanonical };
}

function cardToCanonical(card, suitMapping) {
  if (!card || !suitMapping) return card;
  const rank = card[0];
  const suit = String(card[1] || "").toLowerCase();
  return `${rank}${suitMapping.toCanonical[suit] || suit}`;
}

function canonicalizeUserFlopTo1755(cards) {
  const sorted = (cards || [])
    .slice()
    .sort((a, b) => {
      const ra = RANK_VALUE[a?.[0]] || 0;
      const rb = RANK_VALUE[b?.[0]] || 0;
      if (rb !== ra) return rb - ra;
      return SOLVER_SUITS.indexOf(String(a?.[1] || "").toLowerCase()) - SOLVER_SUITS.indexOf(String(b?.[1] || "").toLowerCase());
    });
  const suitMap = createSuitMapping(sorted);
  const canonicalCards = sorted.map((card) => cardToCanonical(card, suitMap));
  return { canonicalCards, canonicalStr: canonicalCards.join("") };
}

function createSuitMappingBetweenFlops(desiredFlopCards, queriedFlopCards) {
  const desired = (desiredFlopCards || []).slice(0, 3);
  const queried = (queriedFlopCards || []).slice(0, 3);
  const suitCounts = (cards) => {
    const out = {};
    for (const c of cards) {
      const s = c?.[1]?.toLowerCase();
      if (!s) continue;
      out[s] = (out[s] || 0) + 1;
    }
    return out;
  };
  const maxSuitCount = (cards) => {
    const vals = Object.values(suitCounts(cards));
    return vals.length ? Math.max(...vals) : 0;
  };
  const sortSuitsByCountDesc = (counts) =>
    Object.entries(counts)
      .sort((a, b) => (b[1] - a[1]) || (SOLVER_SUITS.indexOf(a[0]) - SOLVER_SUITS.indexOf(b[0])))
      .map(([s]) => s);
  const assign = (toCanonical, fromCanonical, desiredSuit, queriedSuit) => {
    if (!desiredSuit || !queriedSuit) return;
    if (toCanonical[desiredSuit] && toCanonical[desiredSuit] !== queriedSuit) return;
    if (fromCanonical[queriedSuit] && fromCanonical[queriedSuit] !== desiredSuit) return;
    toCanonical[desiredSuit] = queriedSuit;
    fromCanonical[queriedSuit] = desiredSuit;
  };
  const toCanonical = {};
  const fromCanonical = {};
  const dMax = maxSuitCount(desired);
  const qMax = maxSuitCount(queried);
  if (dMax === 3 && qMax === 3) {
    assign(toCanonical, fromCanonical, desired[0]?.[1]?.toLowerCase(), queried[0]?.[1]?.toLowerCase());
  }
  if (dMax === 2 && qMax === 2) {
    const dOrder = sortSuitsByCountDesc(suitCounts(desired));
    const qOrder = sortSuitsByCountDesc(suitCounts(queried));
    assign(toCanonical, fromCanonical, dOrder[0], qOrder[0]);
    assign(toCanonical, fromCanonical, dOrder[1], qOrder[1]);
  }
  if (dMax === 1 && qMax === 1) {
    const sortCards = (cards) => cards
      .slice()
      .sort((a, b) => (RANK_VALUE[b[0]] - RANK_VALUE[a[0]]) || (SOLVER_SUITS.indexOf(String(a[1] || "").toLowerCase()) - SOLVER_SUITS.indexOf(String(b[1] || "").toLowerCase())));
    const dSorted = sortCards(desired);
    const qSorted = sortCards(queried);
    for (let i = 0; i < 3; i++) {
      assign(toCanonical, fromCanonical, dSorted[i]?.[1]?.toLowerCase(), qSorted[i]?.[1]?.toLowerCase());
    }
  }
  const remainingDesired = SOLVER_SUITS.filter((s) => !toCanonical[s]);
  const remainingQueried = SOLVER_SUITS.filter((s) => !fromCanonical[s]);
  for (let i = 0; i < Math.min(remainingDesired.length, remainingQueried.length); i++) {
    assign(toCanonical, fromCanonical, remainingDesired[i], remainingQueried[i]);
  }
  return { toCanonical, fromCanonical };
}

function createFlopRankMappingStrengthPaired(desiredFlopCards, queriedFlopCards) {
  const desired = (desiredFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  const queried = (queriedFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  if (desired.length !== 3 || queried.length !== 3) return null;
  const byStrength = (a, b) => (RANK_VALUE[a] || 0) - (RANK_VALUE[b] || 0);
  const dRanks = desired.map((c) => c[0]);
  const qRanks = queried.map((c) => c[0]);
  const dSorted = [...dRanks].sort(byStrength);
  const qSorted = [...qRanks].sort(byStrength);
  const desiredToQueried = {};
  for (let i = 0; i < 3; i++) desiredToQueried[dSorted[i]] = qSorted[i];
  const desiredFlopRankSet = new Set(dRanks);
  const queriedFlopRankSet = new Set(qRanks);
  const desiredOnlyRanks = new Set(dRanks.filter((r) => !queriedFlopRankSet.has(r)));
  const queriedOnlyRanks = new Set(qRanks.filter((r) => !desiredFlopRankSet.has(r)));
  return { desiredToQueried, desiredFlopRankSet, queriedFlopRankSet, desiredOnlyRanks, queriedOnlyRanks };
}

function comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const rankMap = flopRankMap?.desiredToQueried;
  const onFlop = flopRankMap?.desiredFlopRankSet;
  const remapCard = (card) => {
    const r0 = card[0];
    const s = String(card[1] || "").toLowerCase();
    const r = onFlop?.has(r0) && rankMap && rankMap[r0] !== undefined ? rankMap[r0] : r0;
    const mapped = `${r}${s}`;
    return suitMapping ? cardToCanonical(mapped, suitMapping) : mapped;
  };
  return canonicalizeComboHand(remapCard(comboHand.slice(0, 2)) + remapCard(comboHand.slice(2, 4)));
}

function findComboDataInGrid(gridData, apiHandStr) {
  if (!gridData || !apiHandStr) return null;
  const targetKey = canonicalComboKey(String(apiHandStr).slice(0, 2), String(apiHandStr).slice(2, 4));
  for (const row of gridData || []) {
    for (const cell of row || []) {
      for (const combo of cell?.combos || []) {
        const comboKey = canonicalComboKey(String(combo?.hand || "").slice(0, 2), String(combo?.hand || "").slice(2, 4));
        if (comboKey === targetKey) return combo;
      }
    }
  }
  return null;
}

function comboIsStraightOrStraightFlush(boardCards, comboHand) {
  const board = (boardCards || []).filter(Boolean);
  if (!comboHand || comboHand.length < 4 || board.length < 3) return false;
  const cards = [comboHand.slice(0, 2), comboHand.slice(2, 4), ...board];
  const rankSet = new Set(cards.map((c) => c?.[0]).filter(Boolean).map((r) => RANK_VALUE[r] || 0).filter((v) => v > 0));
  return STRAIGHT_WINDOWS.some((w) => w.every((r) => rankSet.has(r)));
}

function shouldSkipStepOneDirect(comboHand, flopRankMap, desiredBoardCards, queriedBoardCards, apiComboStr) {
  if (!comboHand || comboHand.length < 4) return false;
  const donorShifted = !!flopRankMap?.queriedOnlyRanks && (flopRankMap.queriedOnlyRanks.has(comboHand[0]) || flopRankMap.queriedOnlyRanks.has(comboHand[2]));
  if (donorShifted) return true;
  if (!apiComboStr || !desiredBoardCards?.length || !queriedBoardCards?.length) return false;
  return comboIsStraightOrStraightFlush(desiredBoardCards, comboHand) !== comboIsStraightOrStraightFlush(queriedBoardCards, apiComboStr);
}

function highestDesiredRankNotOnQueried(desiredBoardCards, queriedBoardCards) {
  const queriedRanks = new Set((queriedBoardCards || []).filter(Boolean).map((c) => c[0]));
  let best = null;
  for (const c of desiredBoardCards || []) {
    if (!c || c.length < 2) continue;
    const r = c[0];
    if (queriedRanks.has(r)) continue;
    const v = RANK_VALUE[r] || 0;
    if (!best || v > best.v) best = { r, v };
  }
  return best?.r || null;
}

function resolveRunoutCanonicalCard(userCard, desiredBoardCards, queriedBoardCards, suitMapping) {
  if (!userCard || userCard.length < 2) return userCard;
  const desiredRanks = new Set((desiredBoardCards || []).filter(Boolean).map((c) => c[0]));
  const queriedRanks = new Set((queriedBoardCards || []).filter(Boolean).map((c) => c[0]));
  const primaryCanonical = cardToCanonical(userCard, suitMapping);
  const exactCardCollision = (queriedBoardCards || []).includes(primaryCanonical);
  const rankOnlyCollision = queriedRanks.has(primaryCanonical[0]) && !desiredRanks.has(userCard[0]);
  if (exactCardCollision || rankOnlyCollision) {
    const fallbackDesiredRank = highestDesiredRankNotOnQueried(desiredBoardCards, queriedBoardCards);
    if (fallbackDesiredRank) return cardToCanonical(`${fallbackDesiredRank}${userCard[1]}`, suitMapping);
  }
  return primaryCanonical;
}

// ─── Spot detection ──────────────────────────────────────────────────────────

function detectPostflopSpotFromHistory(history = []) {
  const sig = (history || []).filter((h) => String(h?.action || "").toLowerCase() !== "fold");
  if (sig.length === 2) {
    const [a1, a2] = sig;
    if (a1.action === "open" && a2.action === "call" && SEAT_CODE[a1.position] && SEAT_CODE[a2.position]) {
      return `${SEAT_CODE[a1.position]}open${SEAT_CODE[a2.position]}call`;
    }
  }
  if (sig.length === 3) {
    const [a1, a2, a3] = sig;
    if (a1.action === "open" && a2.action === "3bet" && a3.action === "call" && a1.position === a3.position && SEAT_CODE[a1.position] && SEAT_CODE[a2.position]) {
      return `${SEAT_CODE[a2.position]}3bet${SEAT_CODE[a1.position]}call`;
    }
  }
  if (sig.length === 4) {
    const [a1, a2, a3, a4] = sig;
    if (
      a1.action === "open" && a2.action === "3bet" && a3.action === "4bet" && a4.action === "call" &&
      a3.position === a1.position && a4.position === a2.position && SEAT_CODE[a3.position] && SEAT_CODE[a4.position]
    ) {
      return `${SEAT_CODE[a3.position]}4bet${SEAT_CODE[a4.position]}call`;
    }
  }
  return null;
}

function remapSpotForLegacyDb(spot) {
  const key = String(spot || "").trim();
  if (!key) return key;
  const explicitRemaps = {
    LO4betHIcall: "HI4betCOcall",
    LO4betCOcall: "HI4betCOcall",
    LO4betBTNcall: "HI4betBTNcall",
    LO4betSBcall: "HI4betSBcall",
    LO4betBBcall: "HI4betBBcall",
  };
  if (explicitRemaps[key]) return explicitRemaps[key];
  return LEGACY_SPOT_LOOKUP_REMAP[key] || key;
}

// ─── V6 fetch layer (throttled + cached) ─────────────────────────────────────

const nodeCache = new Map(); // `${spot}|${flop}|${nodeId}` -> grid payload
const NODE_CACHE_MAX = 400;
let lastFetchAt = 0;
const MIN_FETCH_GAP_MS = 350; // stay well under the API rate limit

async function throttleGate() {
  const now = Date.now();
  const wait = Math.max(0, lastFetchAt + MIN_FETCH_GAP_MS - now);
  lastFetchAt = now + wait;
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
}

export class AnalysisAccessError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

async function fetchV6Grid({ spot, flop, nodeId }) {
  const cacheKey = `${spot}|${flop}|${nodeId}`;
  if (nodeCache.has(cacheKey)) return nodeCache.get(cacheKey);
  await throttleGate();
  const baseParams = { spot, versionId: SOLVER_V6_VERSION_ID, flop, nodeId };
  const attempts = [{ ...baseParams, ...V6_CONTEXT }, { ...baseParams }];
  let lastError = null;
  for (const params of attempts) {
    const qs = new URLSearchParams(params);
    const res = await fetch(`${SOLVER_API_URL}/api/v6/grid?${qs.toString()}`, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.success) {
      if (nodeCache.size >= NODE_CACHE_MAX) nodeCache.delete(nodeCache.keys().next().value);
      nodeCache.set(cacheKey, data);
      return data;
    }
    if (res.status === 401) throw new AnalysisAccessError("Login required.", "UNAUTHENTICATED");
    if (res.status === 402) throw new AnalysisAccessError("Pro subscription required.", "PAYWALL");
    lastError = new Error(data?.error || `HTTP ${res.status}`);
  }
  throw lastError || new Error("V6 grid lookup failed.");
}

async function lookupQueriedFlopFromApi(canonicalFlop) {
  const canonical = String(canonicalFlop || "").trim();
  if (!canonical || canonical.length !== 6) return null;
  try {
    await throttleGate();
    const qs = new URLSearchParams({ canonical });
    const res = await fetch(`${SOLVER_API_URL}/api/v6/flop-map?${qs.toString()}`, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) return null;
    const queried = String(data?.queriedFlop || "").trim();
    return queried.length === 6 ? queried : null;
  } catch {
    return null;
  }
}

async function fetchTextureResolutionForSingleCombo({ spot, flop, nodeId, desiredBoardCards, queriedBoardCards, desiredCombo4, preferredApiHand }) {
  await throttleGate();
  const body = {
    spot,
    versionId: SOLVER_V6_VERSION_ID,
    flop,
    nodeId,
    ...V6_CONTEXT,
    desiredBoardCards,
    queriedBoardCards,
    comboRequests: [{ desiredCombo4, preferredApiHand }],
  };
  const res = await fetch(`${SOLVER_API_URL}/api/v6/texture-resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok || !payload?.success) return null;
  return payload?.textureByCombo?.[desiredCombo4] || null;
}

function getActionChildNodeId(v6, actionIndex) {
  if (!v6 || actionIndex == null || actionIndex < 0) return null;
  const child = v6?.actionChildren?.[actionIndex];
  if (!child) return null;
  const id = typeof child === "string" ? child : typeof child === "object" && child?.nodeId ? String(child.nodeId) : null;
  // An all-zero id marks a terminal child (fold/all-in call); no node to load.
  if (!id || /^0+$/.test(id)) return null;
  return id;
}

function getChanceChildNodeId(v6, dealtCard) {
  const card = String(dealtCard || "").trim();
  if (!card) return null;
  const children = v6?.chanceChildren;
  if (!children || typeof children !== "object") return null;
  const direct = children[card] || children[card.toLowerCase()] || children[card.toUpperCase()];
  if (direct) {
    if (typeof direct === "string") return direct;
    if (typeof direct === "object" && direct?.nodeId) return String(direct.nodeId);
  }
  return null;
}

// ─── Grading helpers ─────────────────────────────────────────────────────────

const EPS = 1e-9;

/** Build a decision record from an action->freq map and the hero's chosen key. */
function gradeFromFreqs(actionLabels, freqs, chosenIdx, { chosenIsImpliedFold = false } = {}) {
  const rows = actionLabels.map((label, i) => ({ label, freq: Math.max(0, Number(freqs?.[i] ?? 0)) }));
  // Charts store Fold implicitly: the fold mass is whatever the non-fold
  // actions don't use (a hand outside the defend range shows [0,0,0]).
  const nonFoldSum = rows.reduce((s, r) => s + (/fold/i.test(r.label) ? 0 : r.freq), 0);
  const remainderFold = Math.max(0, 1 - nonFoldSum);
  const foldRow = rows.find((r) => /fold/i.test(r.label));
  if (foldRow) foldRow.freq = Math.max(foldRow.freq, remainderFold);
  else rows.push({ label: "Fold", freq: remainderFold });
  const total = rows.reduce((s, r) => s + r.freq, 0);
  // Normalize so pctMap sums to ~100 even when the source vector is partial.
  const norm = total > EPS ? 1 / total : 0;
  const pctMap = {};
  for (const r of rows) pctMap[r.label] = Math.round(r.freq * norm * 1000) / 10;
  let best = rows[0] || { label: "?", freq: 0 };
  for (const r of rows) if (r.freq > best.freq) best = r;
  const chosen = chosenIsImpliedFold
    ? rows.find((r) => /fold/i.test(r.label)) || { label: "Fold", freq: 0 }
    : rows[chosenIdx] || { label: "?", freq: 0 };
  const chosenFreq = chosen.freq * norm;
  const bestFreq = best.freq * norm;
  let outcome;
  if (chosenFreq <= EPS) outcome = "blunder";
  else if (Math.abs(chosenFreq - bestFreq) <= 0.005 || chosen.label === best.label) outcome = "best";
  else outcome = "ok";
  const score = bestFreq > EPS ? Math.round(Math.min(1, chosenFreq / bestFreq) * 100) : 100;
  return {
    outcome,
    score,
    chosenLabel: chosen.label,
    chosenPct: Math.round(chosenFreq * 1000) / 10,
    bestLabel: best.label,
    bestPct: Math.round(bestFreq * 1000) / 10,
    freqDiffPct: Math.round(Math.max(0, bestFreq - chosenFreq) * 1000) / 10,
    pctMap,
  };
}

/** Match hero's real preflop raise size to a chart action (strict, nearest 0.5bb). */
function matchHeroPreflopRaise(levelBb, isAllIn, actions) {
  const allInIdx = actions.findIndex((a) => /all[\s-]?in/i.test(String(a || "")));
  if (isAllIn && allInIdx >= 0) return allInIdx;
  let bestIdx = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < actions.length; i++) {
    const a = String(actions[i] || "");
    if (!/open|raise|bet/i.test(a) || /all[\s-]?in/i.test(a)) continue;
    const n = extractNumber(a);
    if (!(n > 0)) continue;
    const diff = Math.abs(levelBb - n);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  if (bestIdx >= 0 && bestDiff <= HERO_PREFLOP_TOLERANCE_BB) return bestIdx;
  // Very large raises that are effectively all-in still map to the all-in action.
  if (allInIdx >= 0 && levelBb >= 80) return allInIdx;
  return -1;
}

/**
 * Match a real postflop action to a DB action index.
 * DB sizes are half-bb units: chips-in-bb target = extractNumber(dbAction) / 2.
 */
function matchPostflopDbAction(entry, dbActions, bbAmount, { strict }) {
  const actions = dbActions || [];
  const findRe = (re) => actions.findIndex((a) => re.test(String(a || "")));
  if (entry.type === "check") return { idx: findRe(/check/i), offTree: findRe(/check/i) < 0 ? "No check available at this node" : null };
  if (entry.type === "fold") return { idx: findRe(/fold/i), offTree: findRe(/fold/i) < 0 ? "No fold available at this node" : null };
  if (entry.type === "call") return { idx: findRe(/call/i), offTree: findRe(/call/i) < 0 ? "No call available at this node" : null };

  const targetBb = Number(entry.streetTotal || 0) / bbAmount;
  const allInIdx = findRe(/all[\s-]?in/i);
  const numeric = [];
  for (let i = 0; i < actions.length; i++) {
    const a = String(actions[i] || "");
    if (!/bet|raise/i.test(a) || /all[\s-]?in/i.test(a)) continue;
    const n = extractNumber(a);
    if (n > 0) numeric.push({ idx: i, bb: n / 2 });
  }
  if (entry.allIn && allInIdx >= 0) return { idx: allInIdx, offTree: null };
  if (!numeric.length) {
    if (allInIdx >= 0) return { idx: allInIdx, offTree: null };
    return { idx: -1, offTree: "No bet/raise available at this node" };
  }
  let best = numeric[0];
  for (const c of numeric) if (Math.abs(targetBb - c.bb) < Math.abs(targetBb - best.bb)) best = c;
  if (strict && Math.abs(targetBb - best.bb) > HERO_POSTFLOP_TOLERANCE_BB) {
    return { idx: -1, offTree: `Bet size ${targetBb.toFixed(1)}bb has no GTO size within 1bb (nearest ${best.bb.toFixed(1)}bb)` };
  }
  return { idx: best.idx, offTree: null };
}

function describeEntry(entry, bbAmount) {
  if (entry.type === "fold") return "Fold";
  if (entry.type === "check") return "Check";
  const amt = (v) => `${(v / bbAmount).toFixed(1)}bb`;
  if (entry.type === "call") return `Call ${amt(entry.amount || 0)}`;
  if (entry.type === "bet") return `Bet ${amt(entry.streetTotal || entry.amount || 0)}`;
  return `Raise to ${amt(entry.streetTotal || 0)}`;
}

const STREET_LETTER = { fold: "F", check: "X", call: "C", bet: "B", raise: "R" };

// ─── Main per-hand analyzer ──────────────────────────────────────────────────

/**
 * Analyze one parsed (eligible) hand. Returns the stored result document.
 * Throws AnalysisAccessError when auth/subscription blocks the API.
 */
export async function analyzeHand(hand) {
  const bb = hand.bbAmount;
  const positions = hand.positions;
  const heroPos = positions[hand.heroName];
  const heroCards = hand.heroCards;
  const decisions = [];
  const steps = [];
  let offTreeReason = null;
  let offTreeStreet = null;

  const markOffTree = (street, reason) => {
    if (!offTreeReason) {
      offTreeReason = reason;
      offTreeStreet = street;
    }
  };

  // Running pot in $ (blinds already posted).
  let potDollars = hand.sbAmount + hand.bbAmount;

  steps.push({
    kind: "deal",
    street: "preflop",
    board: [],
    potBb: Number((potDollars / bb).toFixed(1)),
    label: `Hero (${String(heroPos).toUpperCase()}) dealt ${heroCards.join(" ")}`,
  });

  // ── Preflop ────────────────────────────────────────────────────────────────
  const treeHistory = []; // trainer-style entries {position, action, raiseSize}
  let raiseCount = 0;
  const folded = new Set();
  const streetLines = { preflop: "", flop: "", turn: "", river: "" };

  for (const entry of hand.streets.preflop) {
    const pos = positions[entry.name];
    const isHero = entry.name === hand.heroName;
    streetLines.preflop += STREET_LETTER[entry.type] || "?";

    let decisionIdx = null;

    if (isHero) {
      if (offTreeReason) {
        decisions.push({
          street: "preflop", actionLabel: describeEntry(entry, bb), outcome: "na",
          reason: offTreeReason, pctMap: null, ev: null, potBb: Number((potDollars / bb).toFixed(1)),
        });
        decisionIdx = decisions.length - 1;
      } else {
        const pre = getPreflopDecisionGrid(treeHistory, { position: pos, raiseCount });
        if (!pre?.grid || !Array.isArray(pre?.actions)) {
          markOffTree("preflop", "No GTO chart covers this preflop spot");
          decisions.push({
            street: "preflop", actionLabel: describeEntry(entry, bb), outcome: "na",
            reason: offTreeReason, pctMap: null, ev: null, potBb: Number((potDollars / bb).toFixed(1)),
          });
          decisionIdx = decisions.length - 1;
        } else {
          const cell = findCellByLabel(pre.grid, handLabelFromTwoCards(heroCards));
          const freqInfo = cell ? getFrequenciesForHand(cell, heroCards) : { frequencies: [] };
          const freqs = freqInfo.frequencies || [];

          let chosenIdx = -1;
          let chosenIsImpliedFold = false;
          let heroOffTree = null;
          if (entry.type === "fold") {
            chosenIdx = pre.actions.findIndex((a) => /fold/i.test(String(a || "")));
            if (chosenIdx < 0) chosenIsImpliedFold = true;
          } else if (entry.type === "check") {
            heroOffTree = "Limped pot is outside the GTO tree";
          } else if (entry.type === "call") {
            if (raiseCount === 0) heroOffTree = "Open-limp is outside the GTO tree";
            else {
              chosenIdx = pre.actions.findIndex((a) => /call/i.test(String(a || "")));
              if (chosenIdx < 0) heroOffTree = "Call is not available at this node";
            }
          } else {
            const levelBb = (entry.streetTotal || 0) / bb;
            chosenIdx = matchHeroPreflopRaise(levelBb, entry.allIn, pre.actions);
            if (chosenIdx < 0) heroOffTree = `Raise to ${levelBb.toFixed(1)}bb has no GTO size within 0.5bb`;
          }

          if (heroOffTree) {
            markOffTree("preflop", heroOffTree);
            decisions.push({
              street: "preflop", actionLabel: describeEntry(entry, bb), outcome: "na",
              reason: heroOffTree, pctMap: null, ev: null, potBb: Number((potDollars / bb).toFixed(1)),
            });
          } else {
            const grade = gradeFromFreqs(pre.actions, freqs, chosenIdx, { chosenIsImpliedFold });
            decisions.push({
              street: "preflop",
              actionLabel: describeEntry(entry, bb),
              potBb: Number((potDollars / bb).toFixed(1)),
              ev: null,
              ...grade,
            });
          }
          decisionIdx = decisions.length - 1;
        }
      }
    }

    // Update tree state for every player (villains snap onto the tree).
    if (entry.type === "fold") {
      folded.add(pos);
      treeHistory.push({ position: pos, action: "fold" });
    } else if (entry.type === "check") {
      if (!isHero) markOffTree("preflop", "Limped pot is outside the GTO tree");
    } else if (entry.type === "call") {
      if (raiseCount === 0) {
        if (!isHero) markOffTree("preflop", "A player open-limped (outside the GTO tree)");
      } else {
        treeHistory.push({ position: pos, action: "call" });
      }
    } else {
      const level = raiseCount === 0 ? "open" : raiseCount === 1 ? "3bet" : raiseCount === 2 ? "4bet" : "allin";
      const action = entry.allIn && raiseCount >= 3 ? "allin" : level;
      treeHistory.push({ position: pos, action, raiseSize: Number(((entry.streetTotal || 0) / bb).toFixed(2)) });
      raiseCount += 1;
    }

    potDollars += Number(entry.contribution || 0);
    steps.push({
      kind: "action",
      street: "preflop",
      pos,
      isHero,
      label: describeEntry(entry, bb),
      potBb: Number((potDollars / bb).toFixed(1)),
      decisionIdx,
    });
  }

  // ── Postflop setup ─────────────────────────────────────────────────────────
  const stillIn = PREFLOP_ORDER.filter((p) => Object.values(positions).includes(p) && !folded.has(p));
  const heroFolded = folded.has(heroPos);
  const hasPostflopActions = hand.streets.flop.length + hand.streets.turn.length + hand.streets.river.length > 0;
  const wentPostflop = hand.boardCards.length >= 3;

  let postflopReady = false;
  let spot = null;
  let queriedFlop = null;
  let suitMapping = null;
  let flopRankMap = null;
  let desiredBoard = [];
  let queriedBoard = [];
  let heroDesiredCombo4 = null;
  let heroPreferredApiHand = null;

  if (!heroFolded && wentPostflop && !offTreeReason) {
    if (stillIn.length > 2) {
      markOffTree("flop", "Multiway pot, not in database");
    } else {
      const derived = detectPostflopSpotFromHistory(treeHistory);
      spot = remapSpotForLegacyDb(derived);
      if (!spot) {
        markOffTree("flop", "Preflop line has no matching postflop database spot");
      } else {
        const desiredFlopCards = hand.boardCards.slice(0, 3);
        const { canonicalStr } = canonicalizeUserFlopTo1755(desiredFlopCards);
        queriedFlop = await lookupQueriedFlopFromApi(canonicalStr);
        if (!queriedFlop) {
          markOffTree("flop", "Flop texture not found in database");
        } else {
          desiredBoard = [...desiredFlopCards];
          queriedBoard = queriedFlop.match(/.{1,2}/g) || [];
          suitMapping = createSuitMappingBetweenFlops(desiredFlopCards, queriedBoard);
          flopRankMap = createFlopRankMappingStrengthPaired(desiredFlopCards, queriedBoard);
          heroDesiredCombo4 = canonicalizeComboHand(`${heroCards[0]}${heroCards[1]}`);
          heroPreferredApiHand = comboDesiredToApiLookup(heroDesiredCombo4, suitMapping, flopRankMap);
          postflopReady = true;
        }
      }
    }
  }

  // ── Postflop walk ──────────────────────────────────────────────────────────
  let node = null;
  if (postflopReady && hasPostflopActions) {
    try {
      node = await fetchV6Grid({ spot, flop: queriedFlop, nodeId: "root" });
    } catch (err) {
      if (err instanceof AnalysisAccessError) throw err;
      markOffTree("flop", "Could not load the GTO tree for this flop");
      postflopReady = false;
    }
  }

  const resolveHeroComboAtNode = async (gridData, nodeId) => {
    const primary = findComboDataInGrid(gridData, heroPreferredApiHand);
    const forceStepTwo = shouldSkipStepOneDirect(heroDesiredCombo4, flopRankMap, desiredBoard, queriedBoard, heroPreferredApiHand);
    if (!forceStepTwo && isPrimaryApiComboUsable(heroPreferredApiHand, primary, heroDesiredCombo4)) {
      return { frequencies: primary.frequencies, ev: Number(primary.ev || 0) };
    }
    try {
      const tex = await fetchTextureResolutionForSingleCombo({
        spot,
        flop: queriedFlop,
        nodeId,
        desiredBoardCards: desiredBoard,
        queriedBoardCards: queriedBoard,
        desiredCombo4: heroDesiredCombo4,
        preferredApiHand: heroPreferredApiHand,
      });
      if (tex?.fallbackData?.frequencies) {
        return { frequencies: tex.fallbackData.frequencies, ev: Number(tex.fallbackData.ev || 0) };
      }
    } catch (_) {}
    const cell = findCellByLabel(gridData, comboToHandCategory(heroPreferredApiHand));
    if (cell?.frequencies) return { frequencies: cell.frequencies, ev: Number(cell.ev || 0) };
    return null;
  };

  for (const street of ["flop", "turn", "river"]) {
    const entries = hand.streets[street] || [];
    if (!entries.length && street !== "flop") continue;

    // Board reveal step (uses the real cards).
    const boardLen = street === "flop" ? 3 : street === "turn" ? 4 : 5;
    if (hand.boardCards.length >= boardLen) {
      steps.push({
        kind: "board",
        street,
        board: hand.boardCards.slice(0, boardLen),
        potBb: Number((potDollars / bb).toFixed(1)),
        label: street.toUpperCase(),
      });
    }
    if (!entries.length) continue;

    // Advance the tree across the street boundary (chance node -> decision).
    if (postflopReady && !offTreeReason && node && street !== "flop") {
      try {
        if (node?.v6?.kind !== "chance") {
          markOffTree(street, "GTO tree ended before this street");
        } else {
          const desiredCard = hand.boardCards[boardLen - 1];
          const queriedCard = resolveRunoutCanonicalCard(desiredCard, desiredBoard, queriedBoard, suitMapping);
          const childId = getChanceChildNodeId(node.v6, queriedCard);
          if (!childId) {
            markOffTree(street, `The ${street} card could not be mapped into the GTO tree`);
          } else {
            node = await fetchV6Grid({ spot, flop: queriedFlop, nodeId: childId });
            desiredBoard = [...desiredBoard, desiredCard];
            queriedBoard = [...queriedBoard, queriedCard];
          }
        }
      } catch (err) {
        if (err instanceof AnalysisAccessError) throw err;
        markOffTree(street, "Could not advance the GTO tree to this street");
      }
    }

    for (let entryIdx = 0; entryIdx < entries.length; entryIdx++) {
      const entry = entries[entryIdx];
      const laterStreets = street === "flop" ? ["turn", "river"] : street === "turn" ? ["river"] : [];
      const moreActionsRemain =
        entryIdx < entries.length - 1 || laterStreets.some((st) => (hand.streets[st] || []).length > 0);
      const pos = positions[entry.name];
      const isHero = entry.name === hand.heroName;
      streetLines[street] += STREET_LETTER[entry.type] || "?";
      let decisionIdx = null;

      if (isHero) {
        if (offTreeReason || !postflopReady || !node || node?.v6?.kind === "chance") {
          decisions.push({
            street, actionLabel: describeEntry(entry, bb), outcome: "na",
            reason: offTreeReason || "Off the GTO tree", pctMap: null, ev: null,
            potBb: Number((potDollars / bb).toFixed(1)),
          });
          decisionIdx = decisions.length - 1;
        } else {
          const combo = await resolveHeroComboAtNode(node.grid, String(node.nodeId || "root"));
          const match = matchPostflopDbAction(entry, node.actions, bb, { strict: true });
          if (match.idx < 0 || match.offTree) {
            markOffTree(street, match.offTree || "Action not available in the GTO tree");
            decisions.push({
              street, actionLabel: describeEntry(entry, bb), outcome: "na",
              reason: offTreeReason, pctMap: null, ev: null,
              potBb: Number((potDollars / bb).toFixed(1)),
            });
          } else if (!combo) {
            decisions.push({
              street, actionLabel: describeEntry(entry, bb), outcome: "na",
              reason: "Hero combo could not be resolved in the database", pctMap: null,
              ev: null, potBb: Number((potDollars / bb).toFixed(1)),
            });
          } else {
            const grade = gradeFromFreqs(node.actions, combo.frequencies, match.idx);
            decisions.push({
              street,
              actionLabel: describeEntry(entry, bb),
              potBb: Number((potDollars / bb).toFixed(1)),
              ev: Number.isFinite(combo.ev) ? Number(combo.ev.toFixed(2)) : null,
              ...grade,
            });
          }
          decisionIdx = decisions.length - 1;
        }
      }

      // Advance the tree for any actor (hero or villain). Skip the fetch when
      // this is the hand's final action; there is nothing left to grade.
      if (postflopReady && !offTreeReason && node && node?.v6?.kind !== "chance") {
        const match = matchPostflopDbAction(entry, node.actions, bb, { strict: isHero });
        if (match.idx < 0 || match.offTree) {
          markOffTree(street, match.offTree || "Action not available in the GTO tree");
        } else if (entry.type !== "fold" && moreActionsRemain) {
          const childId = getActionChildNodeId(node.v6, match.idx);
          if (!childId) {
            node = null; // terminal (e.g. all-in call); no further decisions exist
          } else {
            try {
              node = await fetchV6Grid({ spot, flop: queriedFlop, nodeId: childId });
            } catch (err) {
              if (err instanceof AnalysisAccessError) throw err;
              markOffTree(street, "Could not load the next GTO node");
              node = null;
            }
          }
        }
      }
      if (entry.type === "fold") folded.add(pos);

      potDollars += Number(entry.contribution || 0);
      steps.push({
        kind: "action",
        street,
        pos,
        isHero,
        label: describeEntry(entry, bb),
        potBb: Number((potDollars / bb).toFixed(1)),
        decisionIdx,
      });
    }
  }

  // ── Result summary ─────────────────────────────────────────────────────────
  const heroSeat = hand.seats.find((s) => s.name === hand.heroName);
  const heroInvested = Number(hand.investedByName[hand.heroName] || 0);
  const heroUncalled = Number(hand.uncalledByName[hand.heroName] || 0);
  const heroCollected = Number(hand.collectedByName[hand.heroName] || 0);
  const winLossBb = Number(((heroCollected + heroUncalled + hand.heroCashoutReceived - hand.heroCashoutRiskPaid - heroInvested) / bb).toFixed(2));

  const graded = decisions.filter((d) => d.outcome !== "na");
  const naCount = decisions.length - graded.length;
  const gtoScore = graded.length ? Math.round(graded.reduce((s, d) => s + Number(d.score || 0), 0) / graded.length) : null;
  const freqDiffPct = graded.length
    ? Math.round((graded.reduce((s, d) => s + Number(d.freqDiffPct || 0), 0) / graded.length) * 10) / 10
    : null;

  // Per-street stats stored on the doc so list views can aggregate without
  // downloading every decision.
  const streetStats = {};
  for (const st of ["preflop", "flop", "turn", "river"]) {
    const ds = graded.filter((d) => d.street === st);
    streetStats[st] = {
      graded: ds.length,
      best: ds.filter((d) => d.outcome === "best").length,
      ok: ds.filter((d) => d.outcome === "ok").length,
      blunders: ds.filter((d) => d.outcome === "blunder").length,
      scoreSum: ds.reduce((s, d) => s + Number(d.score || 0), 0),
    };
  }

  const sig = treeHistory.filter((h) => h.action !== "fold");
  let potType = "Preflop";
  if (wentPostflop && !heroFolded) {
    const raises = sig.filter((h) => /open|3bet|4bet|allin/.test(h.action)).length;
    potType = raises >= 3 ? "4Bet" : raises === 2 ? "3Bet" : raises === 1 ? "SRP" : "Limped";
  }

  if (heroCollected + heroUncalled > 0 || winLossBb !== 0) {
    steps.push({
      kind: "result",
      street: "result",
      potBb: Number((potDollars / bb).toFixed(1)),
      label: winLossBb >= 0 ? `Hero won +${winLossBb.toFixed(2)}bb` : `Hero lost ${winLossBb.toFixed(2)}bb`,
    });
  }

  return {
    handId: hand.handId,
    site: "GG",
    playedAt: hand.playedAt,
    stakes: `${hand.sbAmount}/${hand.bbAmount}`,
    bbAmount: bb,
    heroPos,
    heroCards,
    heroStackBb: heroSeat ? Math.round(heroSeat.stack / bb) : null,
    board: hand.boardCards,
    potBb: Number(((hand.totalPot || potDollars) / bb).toFixed(2)),
    winLossBb,
    potType,
    status: graded.length ? "analyzed" : "na",
    offTreeReason,
    offTreeStreet,
    gtoScore,
    freqDiffPct,
    counts: {
      graded: graded.length,
      best: graded.filter((d) => d.outcome === "best").length,
      ok: graded.filter((d) => d.outcome === "ok").length,
      blunders: graded.filter((d) => d.outcome === "blunder").length,
      na: naCount,
    },
    streetStats,
    streetLines,
    decisions,
    steps,
  };
}

/**
 * Aggregate stats over stored hand summaries (na hands excluded from grading
 * stats). Works from counts/streetStats so the list endpoint doesn't need to
 * ship every decision.
 */
export function summarizeResults(results = []) {
  const analyzed = results.filter((r) => r?.status === "analyzed");
  const naHands = results.filter((r) => r?.status === "na");
  const scoreSum = analyzed.reduce((s, r) => s + Number(r.gtoScore || 0), 0);
  const sumCount = (key) => analyzed.reduce((s, r) => s + Number(r.counts?.[key] || 0), 0);
  const byStreet = {};
  for (const st of ["preflop", "flop", "turn", "river"]) {
    let graded = 0;
    let best = 0;
    let blunders = 0;
    let streetScoreSum = 0;
    for (const r of analyzed) {
      const row = r.streetStats?.[st];
      if (!row) continue;
      graded += Number(row.graded || 0);
      best += Number(row.best || 0);
      blunders += Number(row.blunders || 0);
      streetScoreSum += Number(row.scoreSum || 0);
    }
    byStreet[st] = {
      decisions: graded,
      best,
      blunders,
      avgScore: graded > 0 ? Math.round(streetScoreSum / graded) : null,
    };
  }
  return {
    totalHands: results.length,
    analyzedHands: analyzed.length,
    naHands: naHands.length,
    avgGtoScore: analyzed.length ? Math.round(scoreSum / analyzed.length) : null,
    totalDecisions: sumCount("graded"),
    best: sumCount("best"),
    ok: sumCount("ok"),
    blunders: sumCount("blunders"),
    winLossBb: Number(results.reduce((s, r) => s + Number(r.winLossBb || 0), 0).toFixed(2)),
    byStreet,
  };
}
